import React, { useState, useEffect } from 'react';
import { trackProductView, trackProductSearch } from '../utils/analytics';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Helper function to format price as dollar amount
function formatPrice(price) {
  if (typeof price !== 'number') return '';
  return price % 1 === 0 
    ? `$${price}` 
    : `$${price.toFixed(2)}`;
}

// Helper to format distance
function formatDistance(distance) {
  if (typeof distance !== 'number') return '';
  return distance.toFixed(1) + ' miles away';
}

// Helper to calculate pain metric score (lower is better)
function calculatePainMetric(price, distance) {
  if (typeof price !== 'number' || typeof distance !== 'number') {
    return Infinity; // Worst possible score if we can't calculate
  }
  
  // Simple formula: distance × price
  // This means: 
  // - Double the distance = double the pain
  // - Double the price = double the pain
  // So a $20 product 5 miles away (pain score 100) is equivalent to 
  // a $10 product 10 miles away (pain score 100)
  return price * distance;
}

// Helper to format pain metric
function formatPainMetric(painMetric) {
  if (typeof painMetric !== 'number' || painMetric === Infinity) {
    return '';
  }
  return painMetric.toFixed(0);
}

const ProductSearch = ({ shopperLocation }) => {
  // State for products and filtering
  const [allProducts, setAllProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFiltering, setIsFiltering] = useState(false);
  
  // State for pagination
  const [displayCount, setDisplayCount] = useState(20);
  
  // Filter state - simplified to just product name
  const [productNameFilter, setProductNameFilter] = useState('');
  const [storeInfo, setStoreInfo] = useState({});
  const [sortBy, setSortBy] = useState('price-asc');
  
  // Load products on first mount
  useEffect(() => {
    fetchAllProducts();
  }, []);
  
  // Update store locations when the shopper location changes
  useEffect(() => {
    fetchStoreLocations();
  }, [shopperLocation]);
  
  // Add scroll handler for pagination
  useEffect(() => {
    function handleScroll() {
      // If we're loading or filtering, don't trigger more loading
      if (isLoading || isFiltering) return;
      
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      // If we're near the bottom of the page, load more products
      if (scrollTop + windowHeight + 200 >= documentHeight) {
        setDisplayCount(prevCount => prevCount + 20);
      }
    }

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isLoading, isFiltering]);
  
  // Fetch store locations to get distance information
  const fetchStoreLocations = async () => {
    try {
      // Fetch ALL pickup locations, regardless of geographic area
      const params = new URLSearchParams({
        fields: "fields[]=Name&fields[]=DeliveryURL&fields[]=PickupURL&fields[]=City&fields[]=State&fields[]=Lat&fields[]=Lng&fields[]=DeliveryAreasLookup&fields[]=Image&fields[]=LitAlertsId",
        filter: 'AND({Pickup}=1,{Visible}=1)' // Don't filter by location, get all stores
      });
      
      const response = await fetch(`${API_BASE_URL}/api/locations?${params}`);
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Create a map of store ID to store details with calculated distance
      const storeMap = {};
      
      if (data.records) {
        data.records.forEach(store => {
          if (store.fields.LitAlertsId) {
            const storeId = parseInt(store.fields.LitAlertsId, 10);
            
            // Calculate distance if we have coordinates
            let distance = null;
            if (shopperLocation && shopperLocation.lat && shopperLocation.lng && 
                store.fields.Lat && store.fields.Lng) {
              // Calculate using the distance function
              distance = calculateDistance(
                { lat: parseFloat(shopperLocation.lat), lng: parseFloat(shopperLocation.lng) },
                { lat: parseFloat(store.fields.Lat), lng: parseFloat(store.fields.Lng) }
              );
            }
            
            storeMap[storeId] = {
              name: store.fields.Name,
              city: store.fields.City,
              state: store.fields.State,
              distance: distance
            };
          }
        });
      }
      
      setStoreInfo(storeMap);
    } catch (error) {
      console.error('Error fetching store locations:', error);
    }
  };
  
  // Distance calculation function
  const calculateDistance = (pos1, pos2) => {
    const toRad = (value) => value * Math.PI / 180;
    
    const lat1 = pos1.lat;
    const lat2 = pos2.lat;
    const lon1 = pos1.lng;
    const lon2 = pos2.lng;
    
    const radlat1 = toRad(lat1);
    const radlat2 = toRad(lat2);
    const theta = lon1 - lon2;
    const radtheta = toRad(theta);
    let dist = Math.sin(radlat1) * Math.sin(radlat2) + 
               Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
    
    if (dist > 1) dist = 1;
    
    dist = Math.acos(dist);
    dist = dist * 180 / Math.PI;
    dist = dist * 60 * 1.1515; // Miles
    
    return dist;
  };
  
  // Fetch all products first
  const fetchAllProducts = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/products`);
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      const data = await response.json();
      setAllProducts(data);
      
      // Initially fetch the cheapest products to display by default
      fetchFilteredProducts(true);
    } catch (error) {
      console.error('Error fetching products:', error);
      setIsLoading(false);
    }
  };
  
  // Modified fetch function to try AND first, then OR if no results
  const fetchFilteredProducts = async (isInitialLoad = false) => {
    if (!productNameFilter && !isInitialLoad) {
      // If no filter is applied and this isn't the initial load, just return
      return;
    }
    
    setIsFiltering(true);
    // Reset display count when starting a new search
    setDisplayCount(20);
    
    try {
      // First try with AND mode
      let resultsFound = false;
      
      if (productNameFilter) {
        // Try exclusive (AND) mode first - get up to 1000 products
        let andUrl = `${API_BASE_URL}/api/products/cheapest?limit=1000&name=${encodeURIComponent(productNameFilter)}&searchMode=exclusive`;
        
        const andResponse = await fetch(andUrl);
        if (!andResponse.ok) {
          throw new Error(`API error: ${andResponse.status}`);
        }
        
        const andData = await andResponse.json();
        
        if (andData.count > 0) {
          // If we found results with AND search, use them
          setFilteredProducts(andData.data);
          resultsFound = true;
          
          // Track search in GA
          trackProductSearch({
            nameFilter: productNameFilter,
            searchMode: 'exclusive',
            resultsCount: andData.count
          });
        } else {
          // If no results with AND, try OR - get up to 1000 products
          let orUrl = `${API_BASE_URL}/api/products/cheapest?limit=1000&name=${encodeURIComponent(productNameFilter)}&searchMode=inclusive`;
          
          const orResponse = await fetch(orUrl);
          if (!orResponse.ok) {
            throw new Error(`API error: ${orResponse.status}`);
          }
          
          const orData = await orResponse.json();
          
          setFilteredProducts(orData.data);
          
          // Track search in GA
          trackProductSearch({
            nameFilter: productNameFilter,
            searchMode: 'inclusive',
            resultsCount: orData.count
          });
        }
      } else {
        // If no product filter, fetch all products (up to 1000)
        let url = `${API_BASE_URL}/api/products/cheapest?limit=1000`;
        
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }
        
        const data = await response.json();
        
        setFilteredProducts(data.data);
      }
    } catch (error) {
      console.error('Error fetching filtered products:', error);
    } finally {
      setIsFiltering(false);
      setIsLoading(false);
    }
  };
  
  // Sort products based on selected criteria
  const sortProductsWithStoreInfo = (products) => {
    // First, filter to only products with store info
    const productsWithStoreInfo = products.filter(product => storeInfo[product.retailerId]);
    
    // Now enhance products with additional data for sorting
    const enhancedProducts = productsWithStoreInfo.map(product => {
      const store = storeInfo[product.retailerId];
      const price = product.configs?.[0]?.salePrice || product.configs?.[0]?.normalPrice || 0;
      const distance = store.distance || 999999; // Default to high distance if unknown
      const painMetric = calculatePainMetric(price, distance);
      
      return {
        ...product,
        _sortData: {
          price,
          distance,
          painMetric
        }
      };
    });
    
    // Sort based on selected criteria
    switch (sortBy) {
      case 'price-asc':
        enhancedProducts.sort((a, b) => a._sortData.price - b._sortData.price);
        break;
      case 'price-desc':
        enhancedProducts.sort((a, b) => b._sortData.price - a._sortData.price);
        break;
      case 'distance-asc':
        enhancedProducts.sort((a, b) => a._sortData.distance - b._sortData.distance);
        break;
      case 'pain-asc':
        enhancedProducts.sort((a, b) => a._sortData.painMetric - b._sortData.painMetric);
        break;
      default:
        // Default to price-asc
        enhancedProducts.sort((a, b) => a._sortData.price - b._sortData.price);
    }
    
    return enhancedProducts;
  };
  
  // Handle sort change
  const handleSortChange = (e) => {
    setSortBy(e.target.value);
  };
  
  // Handle form submission
  const handleFilterSubmit = (e) => {
    e.preventDefault();
    fetchFilteredProducts();
  };
  
  // Clear filters
  const clearFilters = () => {
    setProductNameFilter('');
    fetchFilteredProducts(true);
  };
  
  // Handle product click
  const handleProductClick = (product) => {
    if (product.recreationalURL && product.configs?.[0]?.currentStock > 0) {
      // Get store info for tracking
      const store = {
        fields: {
          Name: storeInfo[product.retailerId]?.name || 'Unknown Store',
          City: storeInfo[product.retailerId]?.city || '',
          State: storeInfo[product.retailerId]?.state || ''
        }
      };
      
      trackProductView(product, store);
      window.open(product.recreationalURL, '_blank');
    }
  };
  
  // Format the location display text based on available properties
  const getLocationDisplayName = () => {
    if (!shopperLocation) return 'your location';
    
    // Check for various possible address formats
    if (shopperLocation.formatted_address) {
      return shopperLocation.formatted_address;
    }
    
    // Check for address_components or parts of address
    if (shopperLocation.city) {
      return `${shopperLocation.city}${shopperLocation.state ? `, ${shopperLocation.state}` : ''}`;
    }
    
    // Try to build from lat/lng if that's all we have
    if (shopperLocation.lat && shopperLocation.lng) {
      return `coordinates (${parseFloat(shopperLocation.lat).toFixed(2)}, ${parseFloat(shopperLocation.lng).toFixed(2)})`;
    }
    
    return 'your location';
  };
  
  // Only show location message for distance-related sorts
  const shouldShowLocationMessage = sortBy === 'distance-asc' || sortBy === 'pain-asc';
  
  // Add a message to show when using distance-based sorting
  const locationMessage = shouldShowLocationMessage && shopperLocation ? (
    <div className="location-info">
      Showing distances from {getLocationDisplayName()}
    </div>
  ) : null;
  
  return (
    <div className="store-location product-search-page">
      {/* Filter Form with Sort Options */}
      <div className="filter-container">
        <form onSubmit={handleFilterSubmit} className="filter-form">
          <div className="filter-row">
            <div className="filter-field product-name-field">
              <label htmlFor="product-name">Product Name</label>
              <div className="input-with-button">
                <input
                  type="text"
                  id="product-name"
                  placeholder="Search for products..."
                  value={productNameFilter}
                  onChange={(e) => setProductNameFilter(e.target.value)}
                />
                <button type="submit" className="search-button" disabled={isFiltering}>
                  {isFiltering ? 'Searching...' : 'Search Products'}
                </button>
              </div>
            </div>
            
            <div className="filter-field sort-field">
              <label htmlFor="sort-by">Sort By</label>
              <select 
                id="sort-by"
                value={sortBy}
                onChange={handleSortChange}
              >
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
                <option value="distance-asc">Distance: Closest First</option>
                <option value="pain-asc">Best Value (Price×Distance)</option>
              </select>
              {locationMessage}
            </div>
          </div>
        </form>
      </div>
      
      {/* Results Section */}
      {isLoading ? (
        <div className="loading-message">
          <p>Loading products...</p>
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="no-results-message">
          <h3>No products found</h3>
          <p>Try adjusting your search criteria</p>
        </div>
      ) : (
        <>
          {/* Sort and filter products */}
          {(() => {
            // Sort products with store info based on selected criteria
            const sortedProducts = sortProductsWithStoreInfo(filteredProducts);
            
            // Get the visible products (paginated subset)
            const visibleProducts = sortedProducts.slice(0, displayCount);
            const hasMore = displayCount < sortedProducts.length;
            
            return (
              <>
                <div className="results-count">
                  Found {sortedProducts.length} products
                </div>
                
                <div className="product-list">
                  {visibleProducts.map(product => {
                    const config = product.configs?.[0];
                    const inStock = config && config.currentStock > 0;
                    const onSale = config && config.salePrice && config.salePrice < config.normalPrice;
                    const stockLevel = !config ? 'unknown' : 
                                      config.currentStock <= 0 ? 'out-of-stock' :
                                      config.currentStock <= 5 ? 'low-stock' : 'in-stock';
                    
                    // Get store info (we know it exists because we filtered)
                    const store = storeInfo[product.retailerId];
                    const price = config?.salePrice || config?.normalPrice || 0;
                    const distance = store.distance || null;
                    const painMetric = calculatePainMetric(price, distance);
                    
                    return (
                      <div 
                        key={product.id} 
                        className={`product-item ${stockLevel}`}
                      >
                        <div className="product-content">
                          <h3 className="product-name">{product.name}</h3>
                          
                          <div className="product-price">
                            {onSale ? (
                              <>
                                <span className="original-price">{formatPrice(config.normalPrice)}</span>
                                <span className="sale-price">{formatPrice(config.salePrice)}</span>
                              </>
                            ) : (
                              <span className="regular-price">{formatPrice(config?.normalPrice)}</span>
                            )}
                          </div>
                          
                          <div className="product-stock">
                            {!config ? (
                              "Stock unknown"
                            ) : config.currentStock <= 0 ? (
                              "Out of Stock"
                            ) : (
                              `${config.currentStock} in stock`
                            )}
                          </div>
                          
                          {config?.currentStock <= 5 && config?.currentStock > 0 && (
                            <div className="low-stock-warning">
                              Only {config.currentStock} left!
                            </div>
                          )}
                          
                          <div className="product-location">
                            {store.name} - {store.distance ? (
                              <>
                                {formatDistance(store.distance)}
                                {sortBy === 'pain-asc' && (
                                  <span className="pain-metric" title="Lower is better: Price × Distance">
                                    {" "}(Value Score: {formatPainMetric(painMetric)})
                                  </span>
                                )}
                              </>
                            ) : (
                              `${store.city}, ${store.state}`
                            )}
                          </div>
                          
                          <a 
                            href={product.recreationalURL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="view-details-link"
                            onClick={(e) => {
                              const storeForTracking = {
                                fields: {
                                  Name: store.name,
                                  City: store.city || '',
                                  State: store.state || ''
                                }
                              };
                              trackProductView(product, storeForTracking);
                            }}
                          >
                            View Details
                          </a>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {hasMore && (
                  <div className="loading-more">
                    Scroll for more products...
                  </div>
                )}
              </>
            );
          })()}
        </>
      )}
    </div>
  );
};

export default ProductSearch; 